/** i18n scaffold：zh-TW 為事實來源，en 由 `Dict` 型別強制補齊。 */

export const zhTW = {
  'site.title': 'eLab Gaming Platform',
  'site.tagline': '線上遊戲入口 · 免註冊即可開玩',

  'hero.eyebrow': 'eLab Gaming 入口站',
  'hero.headline': '選一款遊戲，直接進大廳。',
  'hero.body':
    '這裡是 eLab Gaming 的公開遊戲首頁。五子棋、吹牛骰子、剪刀石頭布各自有獨立大廳，可以邀請朋友、開房、或和 AI 對戰。',
  'hero.cta': '瀏覽遊戲',
  'hero.ctaHint': '不需要帳號；每款遊戲會在自己的大廳處理開局與邀請。',
  'hero.secondary': '看全部遊戲',

  'portal.stats': '平台概況',
  'portal.stats.games': '款公開遊戲',
  'portal.stats.rpsFormats': '種剪刀石頭布賽制',
  'portal.stats.noSignup.value': '0',
  'portal.stats.noSignup': '註冊步驟',

  'why.eyebrow': '平台特性',
  'why.title': '入口只是起點，大廳才是開局處',
  'why.fair.title': '可驗證公平',
  'why.fair.body':
    '適合隨機性的遊戲會保留可驗證紀錄，讓玩家事後確認局面不是臨時被改出來的。',
  'why.anon.title': '匿名優先',
  'why.anon.body': '玩一局不需要 email、不需要密碼。進入遊戲大廳後就能建立房間、加入邀請，或找 AI 練手。',
  'why.lobbies.title': '各遊戲獨立大廳',
  'why.lobbies.body': '首頁負責聚合；五子棋、吹牛骰子、剪刀石頭布都從自己的大廳開始，再進入實際對局。',

  'games.eyebrow': 'Game Hubs',
  'games.title': '遊戲目錄',
  'games.intro': '每張卡片都會帶你到該遊戲的大廳站。',
  'games.loading': '載入遊戲目錄中…',
  'games.empty': '目前沒有已上架的遊戲。',
  'games.error': '暫時取不到遊戲目錄',
  'games.retry': '重試',
  'games.liveReady': '剪刀石頭布即時目錄已連線。',
  'games.play': '進入大廳',
  'games.variants': '賽制',
  'games.online': '線上',
  'games.info.imperfect': '不完全訊息',
  'games.info.perfect': '完全訊息',
  'games.timing.simultaneous': '同時出手',
  'games.timing.sequential': '輪流出手',
  'games.anonymousOk': '匿名可玩',

  'game.back': '回目錄',
  'game.leaderboard': '排行榜',
  'game.leaderboard.empty': '還沒有足夠的對局資料',
  'game.rank': '名次',
  'game.player': '玩家',
  'game.wins': '勝',
  'game.losses': '敗',
  'game.draws': '和',

  'auth.login': '登入',
  'auth.logout': '登出',
  'auth.me': '我的紀錄',
  'auth.optional': '登入是選配的 —— 不登入也能玩。',
  'auth.processing': '登入處理中…',
  'auth.failed': '登入失敗',
  'auth.notLoggedIn': '你目前沒有登入。',
  'auth.emailUnverified': '你的 email 還沒完成驗證，部分功能會被限制。',
  'auth.profile': '帳號資料',
  'auth.subject': 'Subject ID',
  'auth.email': 'Email',
  'auth.roles': '角色',
  'auth.record': '對局紀錄',
  'auth.record.pending':
    '玩家自助查詢對局紀錄的端點目前不在 C02 契約裡（見 docs/ai/handoffs/2026-08-14-ws05a-frontend-contract-gaps.md）。在後端補上之前，這裡顯示的是公開排行榜中與你顯示名稱相符的列。',
  'auth.record.none': '公開排行榜中找不到你的紀錄。',

  'lang.switch': '繁體中文',
  'footer.docs': '本站是研究專案的一部分；所有設計文件都在 repo 內。',
  'common.loading': '載入中…',
} as const;

export type StringKey = keyof typeof zhTW;
export type Dict = Record<StringKey, string>;

export const en: Dict = {
  'site.title': 'eLab Gaming Platform',
  'site.tagline': 'Online game hubs · no signup required',

  'hero.eyebrow': 'eLab Gaming portal',
  'hero.headline': 'Pick a game. Enter its lobby.',
  'hero.body':
    'This is the public front door for eLab Gaming. Gomoku, Liar’s Dice, and Rock Paper Scissors each have their own lobby for rooms, invites, friends, and AI matches.',
  'hero.cta': 'Browse games',
  'hero.ctaHint': 'No account needed; each game handles rooms and invites in its own lobby.',
  'hero.secondary': 'See all games',

  'portal.stats': 'Platform snapshot',
  'portal.stats.games': 'public games',
  'portal.stats.rpsFormats': 'RPS formats',
  'portal.stats.noSignup.value': '0',
  'portal.stats.noSignup': 'signup steps',

  'why.eyebrow': 'Platform shape',
  'why.title': 'The portal gathers games; lobbies start matches',
  'why.fair.title': 'Provably fair',
  'why.fair.body':
    'Games with randomness can keep verifiable records, so players can check later that the table state was not invented after the fact.',
  'why.anon.title': 'Anonymous first',
  'why.anon.body':
    'No email or password needed. Enter a lobby, create a room, join an invite, or practice against AI.',
  'why.lobbies.title': 'Independent lobbies',
  'why.lobbies.body': 'The homepage is an aggregator; Gomoku, Liar’s Dice, and Rock Paper Scissors all start in their own lobby before opening a match.',

  'games.eyebrow': 'Game Hubs',
  'games.title': 'Game catalogue',
  'games.intro': 'Each card takes you to that game’s lobby site.',
  'games.loading': 'Loading the catalogue…',
  'games.empty': 'No games are published yet.',
  'games.error': 'Could not load the catalogue right now',
  'games.retry': 'Retry',
  'games.liveReady': 'Rock Paper Scissors live catalogue connected.',
  'games.play': 'Enter lobby',
  'games.variants': 'Formats',
  'games.online': 'Online',
  'games.info.imperfect': 'Imperfect information',
  'games.info.perfect': 'Perfect information',
  'games.timing.simultaneous': 'Simultaneous moves',
  'games.timing.sequential': 'Sequential moves',
  'games.anonymousOk': 'Anonymous OK',

  'game.back': 'Back to catalogue',
  'game.leaderboard': 'Leaderboard',
  'game.leaderboard.empty': 'Not enough matches yet',
  'game.rank': 'Rank',
  'game.player': 'Player',
  'game.wins': 'W',
  'game.losses': 'L',
  'game.draws': 'D',

  'auth.login': 'Sign in',
  'auth.logout': 'Sign out',
  'auth.me': 'My record',
  'auth.optional': 'Signing in is optional — you can play without it.',
  'auth.processing': 'Completing sign-in…',
  'auth.failed': 'Sign-in failed',
  'auth.notLoggedIn': 'You are not signed in.',
  'auth.emailUnverified': 'Your email is not verified yet; some features stay locked.',
  'auth.profile': 'Account',
  'auth.subject': 'Subject ID',
  'auth.email': 'Email',
  'auth.roles': 'Roles',
  'auth.record': 'Match record',
  'auth.record.pending':
    'A self-service player-record endpoint is not in the C02 contract yet (see docs/ai/handoffs/2026-08-14-ws05a-frontend-contract-gaps.md). Until the backend adds one, this shows the public leaderboard rows matching your display name.',
  'auth.record.none': 'No rows for you on the public leaderboard.',

  'lang.switch': 'English',
  'footer.docs': 'This site is part of a research project; all design docs live in the repo.',
  'common.loading': 'Loading…',
};

export const dictionaries: Record<'zh-TW' | 'en', Dict> = {
  'zh-TW': zhTW as unknown as Dict,
  en,
};
